import React, {useMemo} from 'react';
import {Col, Form} from 'react-bootstrap';
import {Icon, Loader} from 'shared/helpers';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {useAppliance} from 'hooks/Product/Appliance';
import {FIELDSET_LAYOUT} from 'components/customer/Product/FieldSet';
import {GenericFields} from 'components/customer/Product/Specs/GenericFields';
import {useFormikContext} from 'formik';
import {useProductContext} from 'contexts/ProductContext';
import {MoreInfo} from 'shared/components/MoreInfo/MoreInfo';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {Appliance as ApplianceInterface} from 'components/customer/Product/entity/Appliance';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {
    RANGEHOOD,
    useGetAppliancesQuery,
} from 'components/customer/Product/store/productApi';
import {
    ApplianceField,
    useFieldHelpers,
} from 'components/customer/Product/Appliance/helpers/useFieldHelpers';

const APPLIANCE_TYPES: {[key: string]: number} = {
    oven: 0,
    rangehood: 1,
    microwave: 2,
};

export interface ApplianceProps {
    dataSourceParams: {
        type: number;
    };
    field: ApplianceField;
    dataSource: ApplianceInterface[];
    dropdown: {
        render: (props: ApplianceProps) => JSX.Element;
    };
}

export const ApplianceOvenAndMicrowave = (props: ApplianceProps) => {
    const {field, dropdown} = props;
    const isSmallDevice = isDeviceSmall();
    const type = APPLIANCE_TYPES[field.options.applianceType];
    const {loader, dialog, buttonClickHandher} = useAppliance(type, field.name);
    const {data: appliances, isLoading} = useGetAppliancesQuery();

    const {values, setFieldValue} = useFormikContext<{
        [key: string]: boolean;
    }>();
    const {productDataStore} = useProductContext();

    const {advancedCheckboxFieldName, formConfig, applianceHandler} =
        useFieldHelpers(type, field);

    const dataSource = useMemo(() => {
        if (appliances && type != undefined) {
            return appliances.filter((appliance) => appliance.type == type);
        }

        return [];
    }, [appliances, type]);

    return (
        <Loader loader={loader || isLoading}>
            <div style={{borderBottom: '1px solid', marginBottom: '15px'}}>
                <h2 style={{width: 'fit-content', padding: '5px 15px'}}>
                    {field.displayName}
                </h2>
            </div>

            <GenericFields
                fields={formConfig}
                fieldsetName="appliance"
                fieldsetLayout={FIELDSET_LAYOUT.COMPOUND}
            />

            {type == RANGEHOOD || values[String(advancedCheckboxFieldName)] ? (
                <FormGroup>
                    <Form.Label column xs={4}>
                        {`${field.displayName} Selection`}

                        <MoreInfo
                            scope={
                                productDataStore?.current &&
                                productDataStore.current.cabinet &&
                                productDataStore.current.cabinet.attributes
                                    ? productDataStore.current.cabinet
                                          .attributes
                                    : {}
                            }
                            info={field.options.moreInfo}
                        />
                    </Form.Label>
                    <Col xs="12" sm="auto" style={{width: 250}}>
                        {dropdown.render({
                            ...props,
                            dataSource,
                            ...{
                                selectHandler: (
                                    name: string,
                                    value: string,
                                    options: ApplianceInterface[]
                                ) => {
                                    applianceHandler(name, value, options);
                                    void setFieldValue(name, value);
                                },
                                hasImage: 'image',
                                extraHandler: applianceHandler,
                                defaultFirstValue: false,
                            },
                        })}
                    </Col>
                    <Col xs="12" sm="1">
                        <OverlayTrigger
                            placement={isSmallDevice ? 'top' : 'right'}
                            overlay="Add a new Appliance">
                            <div>
                                <Icon
                                    style={{
                                        width: '25px',
                                        cursor: 'pointer',
                                        marginTop: '6px',
                                        marginLeft: isSmallDevice
                                            ? '0'
                                            : '-10px',
                                    }}
                                    data-cy="add-new-appliance"
                                    iconName="Button-Plus.svg"
                                    onClick={buttonClickHandher}
                                />
                            </div>
                        </OverlayTrigger>
                    </Col>
                </FormGroup>
            ) : null}
            {dialog}
        </Loader>
    );
};
